import React from 'react';
import { message, Button, Tooltip } from 'antd';
import styled from 'styled-components';
import commonSelectors, { viewersBrowserTabFeatureSelector } from '@store/common/selectors';
import { useSelector, useDispatch } from 'react-redux';
import { get } from 'lodash';
import { createTempDashboard } from '@store/tempDashboard/actions';
import featureNames from '@utils/featureToggle/constants';
import { EventKey } from '@components/session-timeline/TimelineEvents/types';
import { chartIntervalSelector } from '@store/trends/birdChart/selectors';
import moment from 'moment-timezone';
import { calcInterval, isUsableSessionEndTime } from '@store/timeline/utils';
import { DASHBOARD_TYPE, TEMP_DASHBOARD_URL } from '@utils/constants';
import RenderSource from './RenderSource';
import Actions from './Actions';
import { CellContentWrapper } from './styled';

const StyledButton = styled(Button)`
  && {
    padding-left: 0;
  }
`;

interface IProps {
  sessionId: string;
  clientId: string;
  sessionStartTime: string;
  sessionEndTime?: string;
  dataKey: string;
  viewerId: string | null;
  summaries: string[];
  isViewerModule: boolean;
  isLive?: boolean;
}

// Timeline keys for session timeline dashboard
// NOTE: Also used by Nexa dashboard handlers for session timeline creation
export const TIMELINE_KEYS = Object.keys(EventKey);

// Constant summary fields, maybe change later
// NOTE: Also used by Nexa dashboard handlers for session timeline creation
export const CONST_SUMMARY = [
  'ViewerId',
  'ClientId',
  'SessionId',
  'SessionStartTimestampMs',
  'SessionEndTimestampMs',
  'LatestActiveTimestampMs',
  'StartedEarlierThanInterval',
  'OngoingAfterInterval',
  'VideoStartupTimeMs',
  'PlayStatus',
  'LifePlayingTimeMs',
  'LifeRebufferingRatio',
  'LifeRebufferingTimeMs',
  'LifeConnectionInducedRebufferingRatio',
  'LifeConnectionInducedRebufferingTimeMs',
  'LifeAvgBitrateKbps',
  'EndingBitrateKbps',
  'LifeAverageAvgBitrateKbps',
  'LifeAvgFramesPerSec',
  'LifePausedTimePct',
  'LifePausedTimeMs',
  'EndedStatus',
  'LifeLastPlayHeadTimeMs',
  'LifeNumBitrateSwitches',
  'LifeJoinedAvgBitrateKbps',
  'ContentLengthMs',
  'DeviceOS',
  'ISP',
  'AutonomousSystem',
  'ContentCategory',
  'Asset',
  'AppName',
  'AppVersion',
  'DeviceName',
  'DeviceHardwareType',
  'DeviceOSFamily',
  'BrowserName',
  'BrowserVersion',
  'City',
  'State',
  'Country',
  'LifeExitRelatedCIRTimeMs',
];

const RenderViewerSession = ({
  sessionId,
  clientId,
  dataKey,
  sessionStartTime,
  sessionEndTime,
  viewerId,
  isLive = false,
}: IProps) => {
  const { features } = useSelector(commonSelectors);
  let { from } = useSelector(chartIntervalSelector);
  const vmBrowserTabEnabled = useSelector(viewersBrowserTabFeatureSelector);

  /*
    Timeline interval calculation delegated to calcInterval:
    - Usable sessionEndTime → last 24h of the session (+ 30m buffer), capped to now.
    - Missing / unusable sessionEndTime → anchored on sessionStartTime: [start, min(start + 24h, now)].

    Reference tickets:
    https://jira.corp.conviva.com/browse/CSR-6341
    https://jira.corp.conviva.com/browse/CSR-6411
    https://conviva.atlassian.net/browse/CE-6701
  */
  if (sessionStartTime) {
    from = moment(sessionStartTime);
  }

  // https://jira.corp.conviva.com/browse/IF-2239: older or equal to 7 days, disable timeline jump
  const timeIntervalLessSevenDays = moment().diff(from, 'day') < 7;

  const timelineInterval = calcInterval({ sessionStartTime, sessionEndTime });

  const timelineFlag = get(features, [featureNames.VIEWER_DRILLDOWN_TIMELINE], false);
  const dispatch = useDispatch();

  // temp dashboard, internal tab
  const createTimelineTab = () => {
    if (!timelineFlag || !timeIntervalLessSevenDays) {
      message.warning(
        'Session details are available only for last 7 days. Please select a date and time accordingly.',
      );
    } else {
      const body = {
        request: {
          filter: {
            type: 'and',
            filters: [
              {
                type: 'equal',
                dimension: 'CLIENT_ID',
                value: clientId,
              },
              {
                type: 'equal',
                dimension: 'SESSION_ID',
                value: sessionId,
              },
              {
                type: 'equal',
                dimension: 'SESSION_START_TIMESTAMP',
                value: moment(sessionStartTime).toISOString(),
              },
            ],
          },
          // time interval will be overwritten by the dashboard saga based on session start time
          timelineInterval,
          timelines: TIMELINE_KEYS,
          summaries: CONST_SUMMARY,
          options: {},
          meta: {
            sessionStartTime,
            ...(isUsableSessionEndTime(sessionEndTime) ? { sessionEndTime } : {}),
            isLiveSession: isLive,
          },
        },
        ui: {}, // Do need to init this field
        config: {}, // Do need to init this field
      };
      dispatch(createTempDashboard(DASHBOARD_TYPE.TIMELINE, body, `Timeline ${sessionId}`));
    }
  };

  // temp dashbaord, browser tab
  const getNewTabUrl = () => {
    const baseUrl = TEMP_DASHBOARD_URL[DASHBOARD_TYPE.TEMP_TAB];
    const sessionEndParam = isUsableSessionEndTime(sessionEndTime)
      ? `&sessionEndTime=${encodeURIComponent(String(sessionEndTime))}`
      : '';
    const withParams = isLive
      ? `${baseUrl}timeline?clientId=${clientId}&sessionId=${sessionId}&timelineInterval=${timelineInterval}&sessionStartTime=${sessionStartTime}&isLiveSession=${isLive}${sessionEndParam}` // dev note: Link can be used here and params can be passed using search props or state
      : `${baseUrl}timeline?clientId=${clientId}&sessionId=${sessionId}&timelineInterval=${timelineInterval}&sessionStartTime=${sessionStartTime}${sessionEndParam}`;
    return withParams;
  };

  return (
    <CellContentWrapper>
      <div style={{ display: 'inline-block' }}>
        <Tooltip title="Click to see Session Details">
          {vmBrowserTabEnabled ? (
            <a
              style={{ color: '#1890ff', textDecoration: 'none' }}
              href={getNewTabUrl()}
              target="_blank"
              rel="noopener noreferrer"
            >
              <RenderSource data={sessionId} dataKey={dataKey} />
            </a>
          ) : (
            <StyledButton onClick={createTimelineTab} type="link">
              <RenderSource data={sessionId} dataKey={dataKey} />
            </StyledButton>
          )}
        </Tooltip>
      </div>
      <Actions
        data={sessionId}
        clientId={clientId}
        sessionStartTime={sessionStartTime}
        sessionEndTime={sessionEndTime}
        viewerId={viewerId}
      />
    </CellContentWrapper>
  );
};

export default RenderViewerSession;
